"use client";

import MediaImage from "@/components/ui/MediaImage";

import CaseDetailLabels from "@/components/ui/CaseDetailLabels";
import { newCaseLabelStyle } from "@/utils/case-label-style";
import type { CaseLabelSettings } from "@/cms/case-labels";
import { caseMaterialLabel } from "@/utils/case-material";

import { useState } from "react";
import type { CaseData } from "@/cms/models";
import MapWrapper from "@/components/ui/MapWrapper";
import AiMarkingBadge from "@/components/ui/AiMarkingBadge";
import { openContentDialog } from "@/components/ui/ContentDialog";
import { getLabel } from "@/utils/helper";
import CaseMediaViewer from "@/components/ui/CaseMediaViewer";
import {
  defaultCaseGalleryViewer,
  getCaseGalleryImages,
  scrollToCaseImageGrid,
  type CaseGalleryMedia,
  type CaseGalleryViewerSetting,
} from "@/utils/case-gallery";

export default function CinematicCaseGallery({
  caseData,
  viewer,
  gridAnchor,
  labelSettings,
}: {
  caseData: CaseData;
  viewer: CaseGalleryViewerSetting;
  gridAnchor?: string;
  labelSettings?: CaseLabelSettings;
}) {
  const viewerOff = viewer === "none";
  const photosInteractive = !viewerOff || !!gridAnchor;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CaseGalleryMedia>("photo");
  const [map, setMap] = useState(false);
  const galleryImages = getCaseGalleryImages(caseData);
  const photos = galleryImages.filter((image) =>
    image.namespace.endsWith("photo"),
  );
  const drawings = galleryImages.filter((image) =>
    image.namespace.endsWith("drawing"),
  );
  const videos = caseData.videoUrls ?? [];
  const hasMap =
    Number.isFinite(caseData.latitude) && Number.isFinite(caseData.longitude);
  const hero = photos[0];
  const heroUrl = hero?.xlUrl || hero?.lgUrl || caseData.imageUrl;
  const status = caseData.isSold ? "Solgt" : getLabel(caseData);
  const show = (next: typeof view) => {
    if (viewerOff && next === "photo") return scrollToCaseImageGrid();
    setView(next);
    setOpen(true);
  };
  return (
    <>
      <div className="re-cinematic-hero">
        {map && hasMap ? (
          <MapWrapper
            markers={[
              { latitude: caseData.latitude!, longitude: caseData.longitude! },
            ]}
          />
        ) : (
          <>
            {heroUrl && (
              <MediaImage
                src={heroUrl}
                alt={caseData.advertisementAddressOrDisplayName}
                fetchPriority="high"
              />
            )}
            {hero && <AiMarkingBadge image={hero} />}
            {(photos.length > 0 || heroUrl) && photosInteractive && (
              <button
                type="button"
                className="re-cinematic-plus"
                aria-label="Åbn billedgalleri"
                onClick={() => show("photo")}
              >
                +
              </button>
            )}
            {newCaseLabelStyle(labelSettings) ? (
              <CaseDetailLabels caseData={caseData} settings={labelSettings} />
            ) : (
              status && <div className="re-cinematic-status">{status}</div>
            )}
          </>
        )}
      </div>
      <nav
        className="re-cinematic-actions"
        aria-label="Ejendommens medier og dokumenter"
      >
        {photos.length > 0 && photosInteractive && (
          <button
            type="button"
            onClick={() => {
              setMap(false);
              show("photo");
            }}
          >
            Billeder <span aria-hidden>⌃</span>
          </button>
        )}
        {drawings.length > 0 && (
          <button type="button" onClick={() => show("drawing")}>
            Plantegninger <span aria-hidden>⌃</span>
          </button>
        )}
        {videos.length > 0 && (
          <button type="button" onClick={() => show("video")}>
            Video
          </button>
        )}
        {hasMap && (
          <button
            type="button"
            aria-pressed={map}
            onClick={() => setMap((current) => !current)}
          >
            {map ? "Vis billede" : "Vis på kort"} <span aria-hidden>⌖</span>
          </button>
        )}
        {caseData.isActive &&
          !caseData.isSold &&
          caseData.documents?.length > 0 && (
            <button
              type="button"
              onClick={() => openContentDialog(`case-${caseData.id}-documents`)}
            >
              {caseMaterialLabel(caseData, "Bestil salgsopstilling")}
            </button>
          )}
      </nav>
      {open && (
        <CaseMediaViewer
          caseData={caseData}
          variant={
            viewerOff
              ? defaultCaseGalleryViewer({ presentation: "cinematic" })
              : viewer
          }
          view={view}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
